import React, {
  memo,
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { isEqual } from 'lodash';

import { useIsLoading } from 'new/hooks/adapters/useIsLoading';
import { useFeatureFlag } from 'new/hooks/useFeatureFlags';
import useDidUpdateEffect from 'new/hooks/useDidUpdateEffect';
import useFitNodeCreation from 'js/components/pipelines/FlowNodes/useFitNodeCreation';
import usePipelinesItems from 'js/components/pipelines/ExpandedPipelines/hooks/usePipelinesItems';
import { useHashedState } from 'new/state/hashedState/repository/useHashedState';

import { Button } from 'new/elements/Button';
import { Stack } from 'new/elements/Stack';
import Header from 'new/components/header/Header';
import { FlowNodes } from 'js/components/pipelines/FlowNodes';
import { ListItemFlow } from 'js/components/pipelines/ExpandedPipelines/components/ListItemFlow';
import NoContentTextView from 'js/components/elements/NoContentTextView';
import { FlowSelectPopover, FlowSelectionPage } from 'new/components/integrate';
/**
 * Constants
 */
import { GET_PIPELINE_REQUEST, GET_PIPELINES_REQUEST } from 'js/constants/actionTypes';
/**
 * Styles
 */
import classes from './ExpandedPipelines.module.scss';
import styles from 'new/components/integrate/Integrate.module.scss';
import { useRestrictedTiers } from 'new/hooks/useRestrictedTiers';

interface ExpandedPipelinesProps {
  onBack?: () => void;
  pipelines: any[];
  selectedFlow?: number;
  toggleCloneFlowModal: any;
}

const ExpandedPipelines = (props: PropsWithChildren<ExpandedPipelinesProps>) => {
  const { pipelines, toggleCloneFlowModal, onBack } = props;

  const flowNodesRef = useRef(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedFlow, setSelectedFlow] = useState(props.selectedFlow);

  useFitNodeCreation(flowNodesRef, { when: 'mount' });

  const {
    pipelineItems,
    orderItems,
    projects,
    triggeredFlows,
    triggeringMap,
    linkedFlows,
    linksMap,
  } = usePipelinesItems(pipelines);
  const { hasExpressTierLimitedAccess } = useRestrictedTiers();
  const { data } = useHashedState('flows.viewport.flow_nodes_all');

  const flowsOrder = orderItems.sort((a, b) => {
    if (triggeredFlows.includes(a)) return -1;
    if (triggeredFlows.includes(b)) return 1;
    if (linkedFlows.includes(a)) return -1;
    if (linkedFlows.includes(b)) return 1;
    return 0;
  });

  const loading = useIsLoading(GET_PIPELINES_REQUEST, GET_PIPELINE_REQUEST);

  useEffect(() => {
    if (!loading && !data && selectedFlow && flowNodesRef.current) {
      setTimeout(() => {
        fitItem(selectedFlow);
      }, 500);
    }
  }, [loading]);

  useDidUpdateEffect(() => {
    if (!data) {
      flowNodesRef.current.setViewport({ x: 60, y: 0, zoom: 1 });
    }
  }, [data]);

  const fitItem = (flowNodeId, fit = true) => {
    setSelectedFlow(flowNodeId);
    if (fit) {
      const node = flowNodesRef.current.getNode('flow-' + flowNodeId);
      if (!node) return;
      flowNodesRef.current.fitView({
        nodes: [node],
        duration: 500,
      });
    }
  };

  const onFitViewItem = (pipeline, fit = true) => {
    fitItem(pipeline.flow_node_id, fit);
  };

  const handleClose = () => setAnchorEl(null);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) =>
    setAnchorEl(event.currentTarget);

  // See the matching comment in SourcePipeline.tsx — single read point for
  // the whole canvas-building pipeline.
  const byoV2Enabled = useFeatureFlag('UI_ENABLE_BYO_V2');

  const nodeProps = useMemo(
    () => ({
      selectedFlow,
      toggleCloneFlowModal,
      onFitViewItem,
      logsOnlyFocused: true,
      byoV2Enabled,
    }),
    [selectedFlow, toggleCloneFlowModal, byoV2Enabled],
  );

  const flowsList = [...pipelines, ...Object.values(triggeringMap || {})];

  const isPopoverOpen = Boolean(anchorEl);
  const popoverId = isPopoverOpen ? 'simple-popover' : undefined;

  return (
    <>
      <Stack className={classes.container} flex={1} height="100%">
        <Header
          expanded
          onBack={onBack}
          backButton={false}
          rightView={
            hasExpressTierLimitedAccess ? null : (
              <Button
                className={styles.newFlowButtonPopover}
                data-testid="new-data-flow-button"
                variant="contained"
                color="primary"
                size="medium"
                endIcon={isPopoverOpen ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                onClick={handleClick}
              >
                Create New Flow
              </Button>
            )
          }
          handleToggleExpandedView={onBack}
        />
        <div className={classes.searchContainer}>{props.children}</div>
        <Stack height="calc(100% - 118px)" flex={1} direction="row" position="relative">
          {loading ? <div className={classes.loader} /> : null}
          <div className={classes.flowListContainer}>
            {flowsList.map((pipeline) => (
              <ListItemFlow
                key={pipeline.id}
                selectedFlow={selectedFlow}
                selected={selectedFlow === pipeline.flow_node_id}
                onClick={onFitViewItem}
                flow={pipeline}
              />
            ))}
            {!flowsList.length ? (
              <NoContentTextView title={<div>No results match your search criteria.</div>} />
            ) : null}
          </div>
          <Stack className={classes.flowNodesContainer} flex={1}>
            <FlowNodes
              ref={flowNodesRef}
              id="all"
              projects={projects}
              triggeringMap={triggeringMap}
              linksMap={linksMap}
              pipelineItems={pipelineItems}
              flowsOrder={flowsOrder}
              nodeProps={nodeProps}
              isFlowExpanded
              miniMapPosition="bottom-left"
            />
          </Stack>
        </Stack>

        <FlowSelectPopover
          id={popoverId}
          open={isPopoverOpen}
          anchorEl={anchorEl}
          onClose={handleClose}
        >
          <FlowSelectionPage onSelection={handleClose} disableAutoBreadcrumbUpdate />
        </FlowSelectPopover>
      </Stack>
    </>
  );
};

export default memo(ExpandedPipelines, (prevProps, nextProps) => {
  return isEqual(prevProps.pipelines, nextProps.pipelines);
});
